import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Printer,
  Loader2,
  Mail,
  MapPin,
  Monitor,
  XCircle,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { MemoizedIframePreview } from '@/components/treatment/v3/request/package-view/MemoizedIframePreview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  type RecordsRequestEvaluationDetail,
  type RecordsRequestFieldItem,
  useGetRecordsRequestEvaluationQuery,
  usePromoteRecordsRequestPackageMutation,
  useStartRecordsRequestEvaluationMutation,
} from '@/services/api/agentflowService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CHANNEL_ICONS: Record<string, typeof Printer> = {
  fax: Printer,
  email: Mail,
  mail: MapPin,
  portal: Monitor,
};

function formatContactMethod(detail: RecordsRequestEvaluationDetail): string {
  const contactMethod = detail.contact_method;
  if (!contactMethod) return 'Destination unavailable';
  if (contactMethod.recipient_fax_number) return `Fax to ${contactMethod.recipient_fax_number}`;
  if (contactMethod.recipient_email) return `Email to ${contactMethod.recipient_email}`;
  if (contactMethod.recipient_mailing_address) {
    return `Mail to ${contactMethod.recipient_mailing_address}`;
  }
  return contactMethod.channel;
}

function resolvePreviewUrl(pdfUrl: string | null): string | null {
  if (!pdfUrl) {
    return null;
  }

  try {
    return new URL(
      pdfUrl,
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST ?? window.location.origin,
    ).toString();
  } catch {
    return pdfUrl;
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DecisionBanner({ detail }: { detail: RecordsRequestEvaluationDetail }) {
  if (detail.outcome === 'ready_for_submit_review') {
    return (
      <div className="rounded-lg bg-green-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-green-900">Ready to Submit</div>
            <div className="text-xs text-green-700">
              All fields resolved &middot; No blockers
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (detail.outcome === 'blocked_for_human_review') {
    return (
      <div className="rounded-lg bg-amber-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-amber-900">Blocked for Review</div>
            <div className="text-xs text-amber-700">
              {detail.fields.unresolved} unresolved field
              {detail.fields.unresolved !== 1 ? 's' : ''}
              {detail.blockers.length > 0 &&
                ` \u00b7 ${detail.blockers.length} blocker${detail.blockers.length !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        {detail.blockers.length > 0 && (
          <ul className="mt-2 ml-7 space-y-1">
            {detail.blockers.map((b, i) => (
              <li key={i} className="text-xs text-amber-800">
                &bull; {b}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-red-50 px-4 py-3">
      <div className="flex items-center gap-2">
        <XCircle className="h-5 w-5 text-red-600 shrink-0" />
        <div>
          <div className="text-sm font-semibold text-red-900">Evaluation Failed</div>
          <div className="text-xs text-red-700">
            {detail.blockers[0] ?? 'An error occurred during evaluation'}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactMethodSection({ detail }: { detail: RecordsRequestEvaluationDetail }) {
  if (!detail.contact_method) {
    return null;
  }

  const ChannelIcon = CHANNEL_ICONS[detail.contact_method.channel] ?? Printer;
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">
        Recommended Destination
      </div>
      <div className="flex items-start gap-2.5">
        <ChannelIcon className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-800 truncate">
            {formatContactMethod(detail)}
          </div>
          <div className="text-xs text-zinc-500 truncate">{detail.contact_method.provider_name}</div>
        </div>
      </div>
    </div>
  );
}

function PackageContentsSection({ detail }: { detail: RecordsRequestEvaluationDetail }) {
  const totalPages = detail.package_contents.reduce(
    (sum, component) => sum + (component.page_count ?? 0),
    0,
  );
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
          Package Contents
        </span>
        <span className="text-xs text-zinc-500">{totalPages} pg</span>
      </div>
      <div className="space-y-1.5">
        {detail.package_contents.map((component, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
            <span className="text-zinc-700 truncate flex-1">{component.label}</span>
            <span className="text-xs text-zinc-400 shrink-0">
              {component.page_count ?? '-'} pg
            </span>
            {component.signed && (
              <span className="text-[10px] text-green-600 shrink-0">Signed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldItem({ field }: { field: RecordsRequestFieldItem }) {
  const isUnresolved = field.status === 'unresolved';
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-zinc-500 truncate">{field.field_name}</span>
      <span
        className={cn(
          'font-medium truncate text-right max-w-[180px]',
          isUnresolved ? 'text-amber-600' : 'text-zinc-800',
        )}
      >
        {field.value ?? '[[MISSING]]'}
      </span>
    </div>
  );
}

function FieldsSection({ detail }: { detail: RecordsRequestEvaluationDetail }) {
  const [expanded, setExpanded] = useState(false);
  const { fields } = detail;
  const allResolved = fields.unresolved === 0;
  const visibleFields = expanded ? fields.items : fields.items.slice(0, 4);
  const hiddenCount = fields.items.length - 4;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
          Fields Resolved
        </span>
        <span
          className={cn(
            'text-xs font-medium',
            allResolved ? 'text-green-600' : 'text-amber-600',
          )}
        >
          {fields.resolved} / {fields.total}
        </span>
      </div>
      <div className="space-y-1">
        {visibleFields.map((field, i) => (
          <FieldItem key={i} field={field} />
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="mt-2 flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" /> Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> + {hiddenCount} more field
              {hiddenCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}
    </div>
  );
}

function PdfPreviewPanel({ pdfUrl }: { pdfUrl: string | null }) {
  if (!pdfUrl) {
    return (
      <div className="flex flex-1 items-center justify-center bg-zinc-100 rounded-lg">
        <span className="text-sm text-zinc-400">No PDF generated yet</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      <MemoizedIframePreview url={pdfUrl} title="Draft PDF Preview" assumePdf />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------

interface RecordsRequestEvaluationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  treatmentRequestId: string;
  evaluationRunId: string;
}

export const RecordsRequestEvaluationModal = ({
  open,
  onOpenChange,
  matterId,
  treatmentRequestId,
  evaluationRunId,
}: RecordsRequestEvaluationModalProps) => {
  const { data: detail, isLoading } = useGetRecordsRequestEvaluationQuery(
    { matterId, requestId: treatmentRequestId, runId: evaluationRunId },
    { skip: !open },
  );

  const [promote, { isLoading: isPromoting }] = usePromoteRecordsRequestPackageMutation();
  const [startEval, { isLoading: isReEvaluating }] =
    useStartRecordsRequestEvaluationMutation();

  const handlePromote = useCallback(async () => {
    try {
      await promote({
        matterId,
        requestId: treatmentRequestId,
        runId: evaluationRunId,
        request: { open_submission_flow: true },
      }).unwrap();
      toast.success('Draft promoted to active package');
      onOpenChange(false);
    } catch {
      toast.error('Failed to promote package');
    }
  }, [evaluationRunId, matterId, onOpenChange, promote, treatmentRequestId]);

  const handleReEvaluate = useCallback(async () => {
    try {
      await startEval({
        matterId,
        requestId: treatmentRequestId,
      }).unwrap();
      toast.success('Re-evaluation started');
      onOpenChange(false);
    } catch {
      toast.error('Failed to start re-evaluation');
    }
  }, [matterId, onOpenChange, startEval, treatmentRequestId]);

  const completedAgo = detail?.completed_at
    ? formatDistanceToNow(new Date(detail.completed_at), { addSuffix: true })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] min-h-0 max-w-[96vw] flex-col p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-6 py-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={cn(
                'inline-block h-2.5 w-2.5 rounded-full shrink-0',
                detail?.ready_to_submit ? 'bg-green-500' : 'bg-amber-500',
              )}
            />
            <DialogTitle className="text-base font-semibold text-zinc-900 truncate">
              Package Evaluation
            </DialogTitle>
          </div>
          {completedAgo && (
            <span className="text-xs text-zinc-500 shrink-0">{completedAgo}</span>
          )}
        </div>

        {/* Body: split panel */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel */}
          <div className="flex w-[440px] shrink-0 flex-col border-r border-zinc-200">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {isLoading || !detail ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-lg" />
                  ))}
                </div>
              ) : (
                <>
                  <DecisionBanner detail={detail} />
                  <ContactMethodSection detail={detail} />
                  <PackageContentsSection detail={detail} />
                  <FieldsSection detail={detail} />
                </>
              )}
            </div>

            {/* Actions */}
            {detail && (
              <div className="border-t border-zinc-200 px-5 py-3 space-y-2 shrink-0">
                {detail.ready_to_submit ? (
                  <Button
                    className="w-full gap-2"
                    onClick={handlePromote}
                    disabled={isPromoting || isReEvaluating}
                  >
                    {isPromoting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Use This Package
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">
                        <Button className="w-full" disabled>
                          Use This Package
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[240px]">
                      Resolve blockers before using this package
                    </TooltipContent>
                  </Tooltip>
                )}
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleReEvaluate}
                  disabled={isPromoting || isReEvaluating}
                >
                  {isReEvaluating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Re-evaluate
                </Button>
                {!detail.ready_to_submit && (
                  <p className="text-[10px] text-zinc-400 text-center">
                    Uploads draft as the active package and opens submission flow
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right panel: PDF preview */}
          <div className="flex flex-1 flex-col min-h-0 min-w-0 p-3">
            {isLoading || !detail ? (
              <Skeleton className="flex-1 rounded-lg" />
            ) : (
              <PdfPreviewPanel pdfUrl={resolvePreviewUrl(detail.pdf_url)} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
